import { useEffect, useState, useMemo } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { formatDate, formatDateTime } from "../../../i18n/dateLocale";

const CancelledOrderTable = ({orders, callBasicApi, history}) => {
    const [openMenuIndex, setOpenMenuIndex] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'onlineOrderCancelledTime', direction: 'desc' });
    const { t } = useTranslation();

    const toggleMenu = (index) => {
        setOpenMenuIndex(openMenuIndex === index ? null : index);
    };

    useEffect(() => {
        const handleClickOutside = () => {
            if (openMenuIndex !== null) setOpenMenuIndex(null);
        };
        window.addEventListener("click", handleClickOutside);
        return () => window.removeEventListener("click", handleClickOutside);
    }, [openMenuIndex]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedOrders = useMemo(() => {
        if (!orders) return [];
        
        return [...orders].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'userTransactionId':
                    aValue = a.userTransactionId || '';
                    bValue = b.userTransactionId || '';
                    break;
                case 'phone':
                    aValue = a.onlineStoreAddress?.phone || '';
                    bValue = b.onlineStoreAddress?.phone || '';
                    break;
                case 'postalCode':
                    aValue = a.onlineStoreAddress?.postalCode || '';
                    bValue = b.onlineStoreAddress?.postalCode || '';
                    break;
                case 'productName':
                    aValue = a.product?.name || '';
                    bValue = b.product?.name || '';
                    break;
                case 'transactionTime':
                    aValue = new Date(a.transactionTime);
                    bValue = new Date(b.transactionTime);
                    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
                case 'onlineOrderCancelledTime':
                    aValue = new Date(a.onlineOrderCancelledTime);
                    bValue = new Date(b.onlineOrderCancelledTime);
                    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
                case 'cancelReason':
                    aValue = a.onlineOrderCancelReason || '';
                    bValue = b.onlineOrderCancelReason || '';
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [orders, sortConfig]);

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <i className="fas fa-sort ms-1" />;
        return sortConfig.direction === 'asc' ? 
            <i className="fas fa-sort-up ms-1" /> : 
            <i className="fas fa-sort-down ms-1" />;
    };

    return (
        <>
            <Table bordered hover className="table-orders">
                <thead>
                    <tr>
                        <th>{t("common.serialNo")}</th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('userTransactionId')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.orderId")}
                            {getSortIcon('userTransactionId')}
                        </th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('phone')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.userPhone")}
                            {getSortIcon('phone')}
                        </th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('postalCode')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.postcode")}
                            {getSortIcon('postalCode')}
                        </th>
                        <th>{t("admin.orders.table.address")}</th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('productName')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.productName")}
                            {getSortIcon('productName')}
                        </th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('transactionTime')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.orderDate")}
                            {getSortIcon('transactionTime')}
                        </th>
                        <th>{t("admin.orders.table.orderTime")}</th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('onlineOrderCancelledTime')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.cancelDate")}
                            {getSortIcon('onlineOrderCancelledTime')}
                        </th>
                        <th 
                            className="sortable-header" 
                            onClick={() => handleSort('cancelReason')}
                            style={{ cursor: 'pointer' }}
                        >
                            {t("admin.orders.table.cancelReason")}
                            {getSortIcon('cancelReason')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedOrders.map((order, idx) => (
                        <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{order?.userTransactionId}</td>
                            <td>{order?.onlineStoreAddress?.phone}</td>
                            <td>{order?.onlineStoreAddress?.postalCode}</td>
                            <td>{t("common.street")}: {order?.onlineStoreAddress?.street || t("common.na")}, {t("common.address")}: {order?.onlineStoreAddress?.address || t("common.na")}, {t("common.city")}: {order?.onlineStoreAddress?.city || t("common.na")}</td>
                            <td>{order?.product?.name}</td>
                            <td>{formatDate(new Date(order?.transactionTime))}</td>
                            <td>{formatDate(new Date(order?.transactionTime), "p")}</td>
                            <td>{formatDate(new Date(order?.onlineOrderCancelledTime))}</td>
                            <td>{order?.onlineOrderCancelReason}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </>
    )
}

export default CancelledOrderTable;